'use client'

import { motion } from 'framer-motion'
import { cn } from '#app/utils/misc.tsx'

interface AnimatedHamburgerProps {
	isOpen: boolean
	onClick: () => void
	className?: string
	color?: string
	size?: number
}

/**
 * Animated hamburger menu icon that morphs into an X when open.
 * Uses three lines that animate:
 * - Top line rotates 45deg and moves down
 * - Middle line fades out
 * - Bottom line rotates -45deg and moves up
 */
export function AnimatedHamburger({
	isOpen,
	onClick,
	className,
	color = 'currentColor',
	size = 32,
}: AnimatedHamburgerProps) {
	const lineHeight = 2
	const gap = size / 4 // Gap between lines
	const lineWidth = size * 0.75 // Line width relative to container

	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'relative flex items-center justify-center focus:outline-none',
				className,
			)}
			style={{ width: size, height: size }}
			aria-label={isOpen ? 'Close menu' : 'Open menu'}
			aria-expanded={isOpen}
		>
			<div className="relative" style={{ width: lineWidth, height: size * 0.5 }}>
				{/* Top line */}
				<motion.span
					className="absolute left-0 right-0 rounded-full"
					style={{
						height: lineHeight,
						backgroundColor: color,
						top: 0,
					}}
					animate={{
						rotate: isOpen ? 45 : 0,
						y: isOpen ? gap + lineHeight / 2 : 0,
					}}
					transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
				/>

				{/* Middle line */}
				<motion.span
					className="absolute left-0 right-0 rounded-full"
					style={{
						height: lineHeight,
						backgroundColor: color,
						top: '50%',
						marginTop: -lineHeight / 2,
					}}
					animate={{
						opacity: isOpen ? 0 : 1,
						scaleX: isOpen ? 0 : 1,
					}}
					transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
				/>

				{/* Bottom line */}
				<motion.span
					className="absolute left-0 right-0 rounded-full"
					style={{
						height: lineHeight,
						backgroundColor: color,
						bottom: 0,
					}}
					animate={{
						rotate: isOpen ? -45 : 0,
						y: isOpen ? -(gap + lineHeight / 2) : 0,
					}}
					transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
				/>
			</div>
		</button>
	)
}
